"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"

const FAST_INTERVAL = 80
const FAST_PHASE_DURATION = 900
const SLOW_INTERVAL = 240
const PER_DIGIT_SLOW_DELAY = 180
const VISITOR_KEY = "visitor_counted"

export function VisitorCount({ compact = false }: { compact?: boolean }) {
  const sectionRef = useRef<HTMLDivElement | null>(null)
  const timersRef = useRef<{
    fast: Array<ReturnType<typeof setInterval>>
    slow: Array<ReturnType<typeof setInterval>>
    slowDelay: Array<ReturnType<typeof setTimeout>>
    startTimeout?: ReturnType<typeof setTimeout>
  }>({ fast: [], slow: [], slowDelay: [] })

  const [finalCount, setFinalCount] = useState("000000")
  const finalDigits = useMemo(() => finalCount.split(""), [finalCount])
  const [displayDigits, setDisplayDigits] = useState(() => [...finalDigits])
  const [isAnimating, setIsAnimating] = useState(false)
  const [hasAnimated, setHasAnimated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // API URLs
  const API_BASE = "https://dextro-online.com/stjerome_school/API/get_visitor.php"
  const GET_COUNT_URL = `${API_BASE}?action=get_count`
  const INCREMENT_COUNT_URL = `${API_BASE}?action=increment_count`

  const updateDigit = useCallback((index: number, value: string) => {
    setDisplayDigits(prev => {
      if (prev[index] === value) {
        return prev
      }

      const next = [...prev]
      next[index] = value
      return next
    })
  }, [])

  const clearTimers = useCallback(() => {
    timersRef.current.fast.forEach(interval => clearInterval(interval))
    timersRef.current.slow.forEach(interval => clearInterval(interval))
    timersRef.current.slowDelay.forEach(timeout => clearTimeout(timeout))

    timersRef.current.fast = []
    timersRef.current.slow = []
    timersRef.current.slowDelay = []

    if (timersRef.current.startTimeout) {
      clearTimeout(timersRef.current.startTimeout)
    }

    timersRef.current.startTimeout = undefined
  }, [])

  const startAnimation = useCallback(() => {
    clearTimers()
    setIsAnimating(true)
    setHasAnimated(true)

    setDisplayDigits(Array(finalDigits.length).fill("0"))

    const fastIntervals: Array<ReturnType<typeof setInterval>> = []

    finalDigits.forEach((_, index) => {
      let currentDigit = 0

      fastIntervals[index] = setInterval(() => {
        currentDigit = (currentDigit + 1) % 10
        updateDigit(index, currentDigit.toString())
      }, FAST_INTERVAL)
    })

    timersRef.current.fast = fastIntervals

    timersRef.current.startTimeout = setTimeout(() => {
      timersRef.current.fast.forEach(interval => clearInterval(interval))
      timersRef.current.fast = []

      let completedDigits = 0

      finalDigits.forEach((targetDigit, index) => {
        const delayHandle = setTimeout(() => {
          let currentDigit = -1

          const slowInterval = setInterval(() => {
            currentDigit = (currentDigit + 1) % 10
            const nextDigit = currentDigit.toString()
            updateDigit(index, nextDigit)

            if (nextDigit === targetDigit) {
              clearInterval(slowInterval)
              completedDigits += 1

              if (completedDigits === finalDigits.length) {
                setIsAnimating(false)
              }
            }
          }, SLOW_INTERVAL)

          timersRef.current.slow[index] = slowInterval
        }, index * PER_DIGIT_SLOW_DELAY)

        timersRef.current.slowDelay[index] = delayHandle
      })
    }, FAST_PHASE_DURATION)
  }, [clearTimers, finalDigits, updateDigit])

  // Fetch and increment visitor count
  useEffect(() => {
    const fetchAndIncrementCount = async () => {
      try {
        // Check if visitor has already been counted in this session
        const hasVisited = typeof window !== "undefined" && localStorage.getItem(VISITOR_KEY)

        console.log("🔍 Visitor Count Debug:", {
          hasVisited,
          getUrl: GET_COUNT_URL,
          incrementUrl: INCREMENT_COUNT_URL
        })

        if (!hasVisited) {
          // New visitor - increment count
          console.log("📝 New visitor - incrementing count")
          const response = await fetch(INCREMENT_COUNT_URL)

          console.log("📥 Increment Response:", response.status, response.statusText)

          if (response.ok) {
            const data = await response.json()
            console.log("✅ Increment Success - Count:", data.count)
            // Pad count to 6 digits
            const paddedCount = String(data.count).padStart(6, "0")
            setFinalCount(paddedCount)

            // Mark this visitor as counted
            if (typeof window !== "undefined") {
              localStorage.setItem(VISITOR_KEY, "true")
            }
          } else {
            const errorText = await response.text()
            console.error("❌ Increment failed:", errorText)

            // Fallback to GET if increment fails
            console.log("🔄 Falling back to GET")
            const getResponse = await fetch(GET_COUNT_URL)
            if (getResponse.ok) {
              const data = await getResponse.json()
              console.log("✅ GET Success - Count:", data.count)
              const paddedCount = String(data.count).padStart(6, "0")
              setFinalCount(paddedCount)
            } else {
              console.error("❌ GET also failed:", await getResponse.text())
            }
          }
        } else {
          // Return visitor - just fetch the current count
          console.log("👤 Return visitor - getting current count")
          const response = await fetch(GET_COUNT_URL)
          console.log("📥 GET Response:", response.status, response.statusText)

          if (response.ok) {
            const data = await response.json()
            console.log("✅ GET Success - Count:", data.count)
            // Pad count to 6 digits
            const paddedCount = String(data.count).padStart(6, "0")
            setFinalCount(paddedCount)
          } else {
            console.error("❌ GET failed:", await response.text())
          }
        }
      } catch (error) {
        console.error("❌ Error fetching visitor count:", error)
        // Use a default count on error
        setFinalCount("000000")
      } finally {
        setIsLoading(false)
      }
    }

    fetchAndIncrementCount()
  }, [])

  useEffect(() => {
    const element = sectionRef.current
    if (!element || isLoading) {
      return
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isAnimating && !hasAnimated) {
          startAnimation()
        }
      },
      { threshold: 0.5 }
    )

    observer.observe(element)

    return () => {
      observer.disconnect()
    }
  }, [hasAnimated, isAnimating, isLoading])

  useEffect(() => {
    return () => {
      clearTimers()
    }
  }, [clearTimers])

  if (compact) {
    return (
      <div ref={sectionRef} className="flex flex-col items-center justify-center py-0">
        <span className="text-[10px] text-background/60 uppercase tracking-widest mb-0">Visitors</span>
        <div className="text-lg sm:text-xl font-bold bg-gradient-to-r from-red-500 via-red-400 to-red-300 bg-clip-text text-transparent tracking-widest font-mono leading-none">
          {displayDigits.map((digit, index) => (
            <span key={index} className="inline-block mx-[1px]">
              {digit}
            </span>
          ))}
        </div>
      </div>
    )
  }

  return (
    <section ref={sectionRef} className="py-6 sm:py-8">
      <div className="container mx-auto px-4 text-center">
        <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-black mb-3 sm:mb-4 tracking-wider">
          VISITOR COUNTER
        </h2>

        <div className="mb-3 sm:mb-4">
          <div className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-red-800 via-red-700 to-red-600 bg-clip-text text-transparent tracking-wider font-mono">
            {displayDigits.map((digit, index) => (
              <span key={index} className="inline-block mx-0.5 sm:mx-1">
                {digit}
              </span>
            ))}
          </div>
        </div>

        <p className="text-black/90 text-sm sm:text-base lg:text-lg">Thank you for visiting our website!</p>
      </div>
    </section>
  )
}
